<template>
  <div class="web-disabled-page">
    <div class="aurora aurora-left" aria-hidden="true"></div>
    <div class="aurora aurora-right" aria-hidden="true"></div>
    <div class="grain-layer" aria-hidden="true"></div>

    <main class="notice-card">
      <header class="notice-head">
        <p class="badge">Service Notice</p>
        <h1>{{ webDisableNoticeTitle }}</h1>
        <p class="description">{{ webDisableNoticeMessage }}</p>
      </header>

      <section class="qr-panel">
        <div class="qr-frame">
          <img class="mini-program-image" :src="xcxImage" alt="微信小程序二维码" />
        </div>
        <p class="qr-caption">微信扫码进入小程序</p>
      </section>

      <footer class="notice-foot">
        <p class="tips">如需继续使用，请打开微信小程序端。</p>
      </footer>
    </main>
  </div>
</template>

<script setup>
import { webDisableNoticeTitle, webDisableNoticeMessage } from '@/utils/webShutdown'
import xcxImage from '@/images/xcx.png'
</script>

<style scoped>
.web-disabled-page {
  --bg-main: #081121;
  --text-main: #f8fafc;
  --text-body: #c8d5e6;
  --text-muted: #a0b6d2;
  --card-border: rgba(148, 163, 184, 0.3);
  --card-bg: rgba(10, 20, 38, 0.68);
  --card-shadow: rgba(3, 10, 22, 0.58);
  min-height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 26px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at 18% 12%, rgba(16, 185, 129, 0.18), transparent 44%),
    radial-gradient(circle at 84% 83%, rgba(14, 165, 233, 0.2), transparent 46%),
    linear-gradient(140deg, #0a172c 0%, #081121 46%, #0b1b33 100%);
}

.aurora {
  position: absolute;
  pointer-events: none;
  border-radius: 999px;
  filter: blur(36px);
  opacity: 0.48;
}

.aurora-left {
  width: 320px;
  height: 320px;
  left: -96px;
  top: -74px;
  background: radial-gradient(circle, rgba(34, 197, 94, 0.7), rgba(34, 197, 94, 0));
  animation: float-left 12s ease-in-out infinite;
}

.aurora-right {
  width: 360px;
  height: 360px;
  right: -118px;
  bottom: -102px;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.72), rgba(56, 189, 248, 0));
  animation: float-right 14s ease-in-out infinite;
}

.grain-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.12;
  background-image: radial-gradient(rgba(148, 163, 184, 0.25) 0.7px, transparent 0.7px);
  background-size: 5px 5px;
}

.notice-card {
  width: min(690px, 100%);
  position: relative;
  z-index: 1;
  border-radius: 24px;
  border: 1px solid var(--card-border);
  background: var(--card-bg);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  color: var(--text-main);
  box-shadow:
    0 28px 90px -18px var(--card-shadow),
    inset 0 1px 0 rgba(226, 232, 240, 0.22);
  padding: 34px 30px 26px;
  transform: translateY(0);
  animation: card-in 480ms cubic-bezier(0.16, 1, 0.3, 1);
}

.notice-head {
  text-align: center;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b9fbcf;
  border: 1px solid rgba(185, 251, 207, 0.38);
  background: rgba(34, 197, 94, 0.12);
}

h1 {
  margin: 16px 0 0;
  font-size: clamp(27px, 3.2vw, 38px);
  line-height: 1.2;
  letter-spacing: 0.01em;
}

.description {
  margin: 14px auto 0;
  max-width: 560px;
  line-height: 1.78;
  font-size: 16px;
  color: var(--text-body);
}

.qr-panel {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr-frame {
  width: min(320px, 88%);
  border-radius: 18px;
  padding: 12px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.68), rgba(30, 41, 59, 0.4));
  box-shadow:
    0 14px 30px rgba(2, 6, 23, 0.4),
    inset 0 1px 0 rgba(226, 232, 240, 0.18);
}

.mini-program-image {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 10px;
}

.qr-caption {
  margin: 0;
  font-size: 13px;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

.notice-foot {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px dashed rgba(148, 163, 184, 0.28);
}

.tips {
  margin: 0;
  text-align: center;
  font-size: 15px;
  color: #9ac8ff;
}

@keyframes float-left {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(20px, 16px, 0) scale(1.06);
  }
}

@keyframes float-right {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(-18px, -14px, 0) scale(1.04);
  }
}

@keyframes card-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 768px) {
  .web-disabled-page {
    padding: 16px;
  }

  .notice-card {
    border-radius: 16px;
    padding: 24px 18px 20px;
  }

  .qr-frame {
    width: min(290px, 94%);
    border-radius: 14px;
    padding: 10px;
  }

  .tips {
    font-size: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .aurora-left,
  .aurora-right,
  .notice-card {
    animation: none;
  }
}
</style>
